import { Text } from '@chakra-ui/react';
import truncate from 'lodash/truncate';
import { useState } from 'react';

import { Button, Checkbox, Popover, useDisclosure } from '@revelio/ui';

import { SelectionListControls } from '../../../../../shared/selection-lists/components/SelectionListControls/SelectionListControls';
import { usePrimaryFilterState } from '../../../filters/hooks/usePrimaryFilterState';
import type { PrimaryFilterId } from '../../../filters/types/filters';
import type { PrimaryEntity } from '../../PageConfigProvider/types/chart';
import type { CompositionSecondaryDimensionValues } from '../../PageConfigProvider/types/dimension';
import { usePageState } from '../../PageState/PageStateProvider';
import type { SubfilterTree } from '../../PageState/types';
import { useSegmentSelections } from '../ShareOfSegment/ShareOfSegment';
import { useShareOfSegmentSubfilters } from '../ShareOfSegment/useSubFilter';
import styles from './DivergingBarComboControls.module.css';

const Chevron = () => (
  <svg
    width="6"
    height="4"
    viewBox="0 0 6 4"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <g opacity="0.4">
      <path
        d="M5.5 0.5L3 3"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M0.5 0.5L2.9 2.95"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </g>
  </svg>
);

const TRIGGER_LABEL_MAX_LENGTH = 30;

const capitalize = (value: string): string =>
  value.length > 0
    ? `${value.charAt(0).toUpperCase()}${value.slice(1)}`
    : value;

const flattenSubfilterOptions = (list: SubfilterTree[]): SubfilterTree[] =>
  list.flatMap((item) =>
    item.children && item.children.length > 0 ? item.children : [item]
  );

type ComboApply = {
  entity: PrimaryEntity | undefined;
  segments: SubfilterTree[] | undefined;
};

export const DivergingBarComboControls = ({
  plotId,
  primaryFilterId,
  subfilterDimension,
  showEntitySelector,
}: {
  plotId: string;
  primaryFilterId: PrimaryFilterId;
  subfilterDimension: CompositionSecondaryDimensionValues | undefined;
  showEntitySelector: boolean;
}) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { pageState, dispatch } = usePageState();
  const { primaryFilter } = usePrimaryFilterState(primaryFilterId);
  const { selectedSegments, setSelections } = useSegmentSelections(plotId);

  const showSubfilter = subfilterDimension != null;

  const entityOptions: PrimaryEntity[] = (
    primaryFilter?.primarySelections ?? []
  ).map((selection) => ({
    id: selection.id.split('_').pop() || selection.id,
    name: selection.name,
    levelId: selection.levelId,
  }));
  const selectedEntity =
    pageState.primaryEntities?.[plotId] ?? entityOptions[0];

  const handleApply = ({ entity, segments }: ComboApply) => {
    if (entity && entity.id !== selectedEntity?.id) {
      dispatch({
        type: 'SET_PRIMARY_ENTITY',
        payload: { plotId, primaryEntity: entity, fromChartSwitcher: true },
      });
    }
    if (segments) {
      setSelections(segments, { fromChartSwitcher: true });
    }
    onClose();
  };

  const triggerLabel = (() => {
    if (!showSubfilter) return selectedEntity?.name ?? 'Select entity';

    const segmentLabel = (() => {
      if (selectedSegments.length === 0) return 'Select a Segment';
      if (selectedSegments.length === 1) {
        return selectedSegments[0]?.name ?? '';
      }
      return `${selectedSegments.length} selected`;
    })();

    if (!showEntitySelector || !selectedEntity) return segmentLabel;
    return `${selectedEntity.name}: ${segmentLabel}`;
  })();

  if (!showEntitySelector && !showSubfilter) return null;

  return (
    <Popover
      placement="bottom-end"
      open={isOpen}
      onOpenChange={(next) => (next ? onOpen() : onClose())}
      className={styles.popoverContent}
      data-testid="combo-controls-panel"
      trigger={
        <Button
          className={styles.trigger}
          variant="outlined"
          size="xs"
          rightIcon={<Chevron />}
          data-testid="combo-controls-trigger"
          aria-label="Chart controls"
        >
          {truncate(triggerLabel, {
            length: TRIGGER_LABEL_MAX_LENGTH,
            omission: '...',
          })}
        </Button>
      }
    >
      <div className={styles.popoverBody}>
        {subfilterDimension ? (
          <ComboControlsPanelWithSubfilter
            dimension={subfilterDimension}
            entityOptions={showEntitySelector ? entityOptions : []}
            selectedEntity={selectedEntity}
            selectedSegments={selectedSegments}
            onApply={handleApply}
            onClose={onClose}
          />
        ) : (
          <ComboControlsPanel
            entityOptions={entityOptions}
            selectedEntity={selectedEntity}
            segmentLabel=""
            segmentOptions={[]}
            selectedSegments={[]}
            onApply={handleApply}
            onClose={onClose}
          />
        )}
      </div>
    </Popover>
  );
};

const ComboControlsPanelWithSubfilter = ({
  dimension,
  entityOptions,
  selectedEntity,
  selectedSegments,
  onApply,
  onClose,
}: {
  dimension: CompositionSecondaryDimensionValues;
  entityOptions: PrimaryEntity[];
  selectedEntity: PrimaryEntity | undefined;
  selectedSegments: SubfilterTree[];
  onApply: (apply: ComboApply) => void;
  onClose: () => void;
}) => {
  const { subfilterList } = useShareOfSegmentSubfilters(dimension);

  return (
    <ComboControlsPanel
      entityOptions={entityOptions}
      selectedEntity={selectedEntity}
      segmentLabel={capitalize(dimension)}
      segmentOptions={flattenSubfilterOptions(subfilterList)}
      selectedSegments={selectedSegments}
      onApply={onApply}
      onClose={onClose}
    />
  );
};

const ComboControlsPanel = ({
  entityOptions,
  selectedEntity,
  segmentLabel,
  segmentOptions,
  selectedSegments,
  onApply,
  onClose,
}: {
  entityOptions: PrimaryEntity[];
  selectedEntity: PrimaryEntity | undefined;
  segmentLabel: string;
  segmentOptions: SubfilterTree[];
  selectedSegments: SubfilterTree[];
  onApply: (apply: ComboApply) => void;
  onClose: () => void;
}) => {
  const [stagedEntityId, setStagedEntityId] = useState<string | undefined>(
    selectedEntity?.id
  );
  const [stagedSegmentIds, setStagedSegmentIds] = useState<string[]>(() =>
    selectedSegments.map((segment) => segment.id)
  );

  const showEntities = entityOptions.length > 0;
  const showSegments = segmentOptions.length > 0;

  const toggleSegment = (id: string) => {
    setStagedSegmentIds((prev) =>
      prev.includes(id)
        ? prev.filter((selected) => selected !== id)
        : [...prev, id]
    );
  };

  const handleSubmit = () => {
    onApply({
      entity: showEntities
        ? entityOptions.find((option) => option.id === stagedEntityId)
        : undefined,
      segments: showSegments
        ? segmentOptions.filter((option) =>
            stagedSegmentIds.includes(option.id)
          )
        : undefined,
    });
  };

  return (
    <div>
      <div className={styles.columns}>
        {showEntities && (
          <OptionColumn
            label="Entity"
            options={entityOptions}
            selectedIds={stagedEntityId ? [stagedEntityId] : []}
            onToggle={setStagedEntityId}
            testId="combo-entity-options"
          />
        )}
        {showSegments && (
          <OptionColumn
            label={segmentLabel}
            options={segmentOptions}
            selectedIds={stagedSegmentIds}
            onToggle={toggleSegment}
            testId="combo-segment-options"
          />
        )}
      </div>
      <SelectionListControls
        className={styles.controls}
        onSubmit={handleSubmit}
        onClose={onClose}
        submitText="Apply"
        {...(showSegments ? { onClear: () => setStagedSegmentIds([]) } : {})}
      />
    </div>
  );
};

const OptionColumn = ({
  label,
  options,
  selectedIds,
  onToggle,
  testId,
}: {
  label: string;
  options: { id: string; name: string }[];
  selectedIds: string[];
  onToggle: (id: string) => void;
  testId: string;
}) => (
  <div className={styles.column}>
    <Text className={styles.columnLabel}>{label}</Text>
    <div
      className={styles.optionList}
      data-testid={testId}
      role="group"
      aria-label={label}
    >
      {options.map((option) => (
        <Checkbox
          key={option.id}
          checked={selectedIds.includes(option.id)}
          onChange={() => onToggle(option.id)}
        >
          <Text className={styles.optionLabel}>{option.name}</Text>
        </Checkbox>
      ))}
    </div>
  </div>
);
